import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";

const STATS = [
  { value: 600, suffix: "K+", key: "followers" },
  { value: 7, suffix: "M+", key: "views" },
  { value: 8, suffix: "+", key: "years" },
  { value: 3, suffix: "", key: "languages" },
];

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setVal(to);
      return;
    }
    let start = 0;
    const duration = 1800;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * to));
      if (p < 1) requestAnimationFrame(step);
      else setVal(to);
    };
    requestAnimationFrame(step);
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display text-5xl sm:text-7xl lg:text-8xl tracking-tight">
      {val}
      <span style={{ color: "var(--mf-orange)" }}>{suffix}</span>
    </span>
  );
}

export default function SocialProof() {
  const { t } = useI18n();
  return (
    <section
      id="stats"
      className="relative py-20 sm:py-28 border-y border-white/5 overflow-hidden"
      data-testid="stats-section"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url(https://customer-assets.emergentagent.com/job_elite-fitness-jorge/artifacts/f698l9nv_05_social_proof_prosupps.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "grayscale(100%) brightness(0.4)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
        {STATS.map((s, i) => (
          <motion.div
            key={s.key}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="text-center lg:text-left"
            data-testid={`stat-${s.key}`}
          >
            <Counter to={s.value} suffix={s.suffix} />
            <div className="mt-2 text-[10px] sm:text-xs tracking-[0.3em] uppercase text-zinc-400">
              {t.stats[s.key]}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
